import { Link } from 'react-router-dom';
import { Hammer, Shield, Clock, Award, ArrowRight } from 'lucide-react';

const Home = () => {
  const services = [
    {
      title: 'Portones',
      description: 'Portones metálicos personalizados con sistemas de apertura manual o automática.',
      image: 'products/porton batiente.jpg',
    },
    {
      title: 'Rejas',
      description: 'Rejas de seguridad para ventanas y puertas con diseños modernos y resistentes.',
      image: 'products/Reja para ventanas.jpg',
    },
    {
      title: 'Techos para Autos',
      description: 'Estructuras metálicas para cocheras y estacionamientos con materiales de primera calidad.',
      image: 'products/techo auto.jpg',
    },
  ];

  const features = [
    {
      icon: Shield,
      title: 'Calidad Garantizada',
      description: 'Materiales de primera calidad y acabados profesionales en cada proyecto.',
    },
    {
      icon: Clock,
      title: 'Entrega Puntual',
      description: 'Cumplimos con los plazos acordados sin comprometer la calidad del trabajo.',
    },
    {
      icon: Award,
      title: 'Experiencia Comprobada',
      description: 'Años de experiencia respaldando cada proyecto que realizamos.',
    },
    {
      icon: Hammer,
      title: 'Trabajo Artesanal',
      description: 'Atención al detalle y precisión en cada soldadura y acabado.',
    },
  ];

  const projects = [
    {
      image: 'products/porton corredizo.jpg',
      title: 'Portón Corredizo Industrial',
      category: 'Portones',
    },
    {
      image: 'products/estructura.png',
      title: 'Estructura Metálica para Cochera',
      category: 'Estructuras',
    },
    {
      image: 'products/porton batiente.jpg',
      title: 'Portón Residencial',
      category: 'Portones',
    },
    {
      image: 'products/puerta.png',
      title: 'Rejas de Seguridad',
      category: 'Rejas',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1745448797901-2a4c9d9af1c1?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Soldador profesional trabajando"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-800/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight uppercase text-white mb-6">
            Soluciones Metalúrgicas <br />de Alta Precisión
          </h1>
          <p className="text-lg md:text-xl text-slate-200 mb-8 max-w-2xl mx-auto">
            Especialistas en portones, rejas, techos y estructuras metálicas con la mejor calidad del mercado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/productos"
              data-testid="hero-cta-products"
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm px-8 py-4 font-bold uppercase tracking-wider hover-lift hard-shadow-blue inline-flex items-center justify-center gap-2"
            >
              Ver Productos <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contacto"
              data-testid="hero-cta-contact"
              className="bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-50 rounded-sm px-8 py-4 font-bold uppercase tracking-wider hover-lift hard-shadow inline-flex items-center justify-center gap-2"
            >
              Contactar
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4">Nuestros Servicios</h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Ofrecemos una amplia gama de servicios de metalurgia con los más altos estándares de calidad.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="group relative bg-white border border-slate-200 hover:border-slate-900 transition-all duration-300 flex flex-col h-full overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-normal text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-6">{service.description}</p>
                  <Link
                    to="/productos"
                    className="mt-auto text-blue-700 font-medium uppercase tracking-wide inline-flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    Ver más <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4">¿Por Qué Elegirnos?</h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Compromiso con la excelencia en cada proyecto que realizamos.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} data-testid={`feature-card-${index}`} className="bg-white p-8 border-l-4 border-blue-700">
                  <Icon className="w-12 h-12 text-blue-700 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-base leading-relaxed text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects Gallery */}
      <section className="py-20 md:py-32 bg-white" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4">Trabajos Realizados</h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Una muestra de nuestros proyectos más destacados.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {projects.map((project, index) => (
              <div key={index} data-testid={`project-card-${index}`} className="group relative overflow-hidden aspect-square">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <div className="p-6">
                    <p className="text-xs font-medium tracking-widest uppercase text-slate-400 mb-1">{project.category}</p>
                    <h3 className="text-lg font-semibold text-white">{project.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6">¿Listo para tu Proyecto?</h2>
          <p className="text-lg md:text-xl text-slate-300 mb-8">
            Contáctanos hoy mismo y obtén una cotización sin compromiso para tu proyecto de metalurgia.
          </p>
          <Link
            to="/contacto"
            data-testid="cta-contact-button"
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-sm px-8 py-4 font-bold uppercase tracking-wider hover-lift hard-shadow inline-flex items-center gap-2"
          >
            Solicitar Cotización <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;



